import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Profile from '../Image/miles-morales-main.png';
import { FaGithub, FaLinkedin, FaTerminal, FaTiktok } from 'react-icons/fa';
import { SiX } from 'react-icons/si';
import { motion, AnimatePresence } from 'framer-motion';
import WhatsAppLink from '../utils/WhatsappLink';

function HomeComponent() {
  const [activeTab, setActiveTab] = useState<'profile' | 'skills' | 'status'>('profile');
  const [terminalOutput, setTerminalOutput] = useState<string[]>([
    "amiola-dev init",
    "Loading dependencies... [OK]",
    "System fully operational. Ready to ship."
  ]);
  const [terminalInput, setTerminalInput] = useState("");

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleRunCommand = (e: React.FormEvent) => {
    e.preventDefault();
    if (!terminalInput.trim()) return;

    const cmd = terminalInput.trim().toLowerCase();
    let response = `Command not found: ${cmd}. Try: 'help', 'ship', 'skills', 'clear'.`;

    if (cmd === 'help') {
      response = "Available commands: ship, skills, status, clear, help";
    } else if (cmd === 'ship') {
      response = "🚀 Shipping Confess v2... App deployed to https://confess.com.ng! (100% test coverage)";
    } else if (cmd === 'skills') {
      response = "Frontend: React, Next.js, TS | Backend: Node.js, AdonisJS | Web3: Solidity";
    } else if (cmd === 'status') {
      response = "🟢 Currently building Web3 bridge and expanding Confess anonymous reach.";
    } else if (cmd === 'clear') {
      setTerminalOutput([]);
      setTerminalInput("");
      return;
    }

    setTerminalOutput(prev => [...prev, `> ${terminalInput}`, response]);
    setTerminalInput("");
  };

  return (
    <div className="relative min-h-screen pt-24 pb-16 flex flex-col justify-center overflow-hidden bg-grid-pattern">
      <div className="glow-spot top-1/4 left-1/4 -translate-x-1/2 -translate-y-1/2"></div>
      <div className="glow-spot bottom-1/4 right-1/4 translate-x-1/2 translate-y-1/2"></div>

      <div className="max-w-6xl mx-auto w-full px-5 md:px-10 lg:px-24 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center relative z-10">
        
        {/* Left Column: Heading and description */}
        <motion.div 
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
          className="lg:col-span-7 flex flex-col space-y-6 text-start"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-teal-500/20 bg-teal-500/5 text-teal-600 dark:text-teal-400 text-xs font-semibold tracking-wide w-fit">
            <span className="w-1.5 h-1.5 rounded-full bg-teal-500 animate-ping"></span>
            Available for new opportunities
          </div>

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.1] text-slate-900 dark:text-white">
            Software developer who turns <span className="text-teal-600 dark:text-teal-400">ideas</span> into products people use.
          </h1>

          <p className="text-base md:text-lg text-slate-600 dark:text-slate-400 leading-relaxed max-w-xl">
            I build modern web experiences and enjoy working across frontend, backend, Web3, and product development. I focus on creating clean, robust codebases that deliver tangible product success.
          </p>

          <div className="flex flex-wrap gap-4 pt-2">
            <Link to="/portfolio" className="px-6 py-3 rounded-full bg-slate-900 hover:bg-slate-800 text-white dark:bg-teal-500 dark:hover:bg-teal-400 dark:text-slate-950 font-semibold text-sm shadow-md transition-all duration-200">
              View my work
            </Link>
            <Link to="/contact" className="px-6 py-3 rounded-full border border-slate-300 dark:border-slate-800 bg-white/50 dark:bg-slate-900/50 hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-800 dark:text-white font-semibold text-sm transition-all duration-200">
              Let's work together
            </Link>
          </div>

          {/* Social Links */}
          <div className="flex items-center gap-5 pt-4 text-slate-600 dark:text-slate-400">
            <a href="https://github.com/feranmiba" target="_blank" rel="noopener noreferrer" className="hover:text-teal-600 dark:hover:text-teal-400 transition-colors" aria-label="GitHub">
              <FaGithub size={22} />
            </a>
            <a href="https://www.linkedin.com/in/amiola-demilade-5a46801a8/" target="_blank" rel="noopener noreferrer" className="hover:text-teal-600 dark:hover:text-teal-400 transition-colors" aria-label="LinkedIn">
              <FaLinkedin size={22} />
            </a>
            <a href="https://x.com/emmanuel_amiola" target="_blank" rel="noopener noreferrer" className="hover:text-teal-600 dark:hover:text-teal-400 transition-colors" aria-label="X (formerly Twitter)">
              <SiX size={20} />
            </a>
            <a href="https://www.tiktok.com/@amidev2" target="_blank" rel="noopener noreferrer" className="hover:text-teal-600 dark:hover:text-teal-400 transition-colors" aria-label="TikTok">
              <FaTiktok size={20} />
            </a>
          </div>
        </motion.div>

        {/* Right Column: Code Editor Widget + Profile */}
        <motion.div 
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, ease: 'easeOut', delay: 0.2 }}
          className="lg:col-span-5 flex flex-col space-y-6"
        >
          {/* Interactive IDE / Terminal Widget */}
          <div className="w-full glass rounded-2xl shadow-xl overflow-hidden border border-slate-200/80 dark:border-slate-800/80 text-xs text-left">
            {/* Header window control buttons */}
            <div className="px-4 py-3 flex items-center justify-between border-b border-slate-200/85 dark:border-slate-800/85 bg-slate-100/50 dark:bg-slate-900/40">
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-red-400/90 block"></span>
                <span className="w-3 h-3 rounded-full bg-yellow-400/90 block"></span>
                <span className="w-3 h-3 rounded-full bg-green-400/90 block"></span>
              </div>
              <span className="code-font text-slate-500 font-medium">amiola_dev_workspace</span>
              <div className="w-12"></div>
            </div>

            {/* IDE Tabs */}
            <div className="flex bg-slate-100/20 dark:bg-slate-900/20 border-b border-slate-200/85 dark:border-slate-800/85 code-font">
              <button 
                onClick={() => setActiveTab('profile')} 
                className={`px-4 py-2 border-r border-slate-200/85 dark:border-slate-800/85 hover:bg-slate-100/50 dark:hover:bg-slate-800/50 transition-colors ${activeTab === 'profile' ? 'bg-white dark:bg-slate-950 font-medium border-t-2 border-t-teal-500 text-teal-600 dark:text-teal-400' : 'text-slate-500'}`}
              >
                profile.json
              </button>
              <button 
                onClick={() => setActiveTab('skills')} 
                className={`px-4 py-2 border-r border-slate-200/85 dark:border-slate-800/85 hover:bg-slate-100/50 dark:hover:bg-slate-800/50 transition-colors ${activeTab === 'skills' ? 'bg-white dark:bg-slate-950 font-medium border-t-2 border-t-teal-500 text-teal-600 dark:text-teal-400' : 'text-slate-500'}`}
              >
                stack.ts
              </button>
              <button 
                onClick={() => setActiveTab('status')} 
                className={`px-4 py-2 hover:bg-slate-100/50 dark:hover:bg-slate-800/50 transition-colors flex items-center gap-1.5 ${activeTab === 'status' ? 'bg-white dark:bg-slate-950 font-medium border-t-2 border-t-teal-500 text-teal-600 dark:text-teal-400' : 'text-slate-500'}`}
              >
                <FaTerminal size={10} /> terminal
              </button>
            </div>

            {/* Editor Body */}
            <div className="p-5 min-h-[180px] code-font bg-white/40 dark:bg-slate-950/40 text-slate-700 dark:text-slate-300">
              <AnimatePresence mode="wait">
                {activeTab === 'profile' && (
                  <motion.pre 
                    key="profile"
                    initial={{ opacity: 0, y: 5 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -5 }}
                    transition={{ duration: 0.2 }}
                    className="whitespace-pre-wrap leading-relaxed"
                  >
                    {`{\n  `}
                    <span className="text-teal-600 dark:text-teal-400">"name"</span>{`: `}
                    <span className="text-amber-600 dark:text-amber-400">"Amiola Demilade"</span>{`,\n  `}
                    <span className="text-teal-600 dark:text-teal-400">"role"</span>{`: `}
                    <span className="text-amber-600 dark:text-amber-400">"Software Developer"</span>{`,\n  `}
                    <span className="text-teal-600 dark:text-teal-400">"focus"</span>{`: `}
                    <span className="text-amber-600 dark:text-amber-400">"Turning product ideas into robust web tools"</span>{`,\n  `}
                    <span className="text-teal-600 dark:text-teal-400">"flagshipProject"</span>{`: `}
                    <span className="text-amber-600 dark:text-amber-400">"Confess (Anonymous audio & text messaging)"</span>{`,\n  `}
                    <span className="text-teal-600 dark:text-teal-400">"shipPower"</span>{`: `}
                    <span className="text-amber-600 dark:text-amber-400">"100% continuous deployment ready"</span>
                    {`\n}`}
                  </motion.pre>
                )}

                {activeTab === 'skills' && (
                  <motion.pre 
                    key="skills"
                    initial={{ opacity: 0, y: 5 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -5 }}
                    transition={{ duration: 0.2 }}
                    className="whitespace-pre-wrap leading-relaxed"
                  >
                    <span className="text-purple-600 dark:text-purple-400">const</span>{' '}
                    <span className="text-blue-600 dark:text-blue-400">skills</span>{' = {\n  '}
                    <span className="text-teal-600 dark:text-teal-400">frontend</span>{`: [`}
                    <span className="text-amber-600 dark:text-amber-400">"React", "Next.js", "TypeScript", "Tailwind"</span>{`],\n  `}
                    <span className="text-teal-600 dark:text-teal-400">backend</span>{`: [`}
                    <span className="text-amber-600 dark:text-amber-400">"Node.js", "AdonisJS"</span>{`],\n  `}
                    <span className="text-teal-600 dark:text-teal-400">databases</span>{`: [`}
                    <span className="text-amber-600 dark:text-amber-400">"PostgreSQL", "MongoDB"</span>{`],\n  `}
                    <span className="text-teal-600 dark:text-teal-400">web3</span>{`: [`}
                    <span className="text-amber-600 dark:text-amber-400">"Solidity", "Smart Contracts"</span>{`]\n};`}
                  </motion.pre>
                )}

                {activeTab === 'status' && (
                  <motion.div 
                    key="status"
                    initial={{ opacity: 0, y: 5 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -5 }}
                    transition={{ duration: 0.2 }}
                    className="flex flex-col space-y-2 h-full justify-between"
                  >
                    <div className="overflow-y-auto max-h-[140px] space-y-1 text-[11px] text-slate-500 dark:text-slate-400">
                      {terminalOutput.map((out, idx) => (
                        <div key={idx} className={out.startsWith('>') ? "text-slate-700 dark:text-slate-200" : out.startsWith('🚀') || out.startsWith('🟢') ? "text-teal-600 dark:text-teal-400" : ""}>
                          {out}
                        </div>
                      ))}
                    </div>
                    <form onSubmit={handleRunCommand} className="flex gap-2 border-t border-slate-200/80 dark:border-slate-800/80 pt-2">
                      <span className="text-slate-500 font-semibold">&gt;</span>
                      <input 
                        type="text" 
                        value={terminalInput}
                        onChange={(e) => setTerminalInput(e.target.value)}
                        placeholder="Type 'help' and press Enter..." 
                        className="bg-transparent outline-none flex-1 text-slate-800 dark:text-slate-200"
                      />
                    </form>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* Profile Card Overlay */}
          <div className="relative group mx-auto lg:mx-0 w-64 h-64 md:w-72 md:h-72 self-center flex items-center justify-center">
            {/* Pulsing visual glow backdrop */}
            <div className="absolute inset-0 rounded-full bg-gradient-to-tr from-teal-500/10 to-transparent blur-2xl group-hover:from-teal-500/20 transition-all duration-500"></div>
            <div className="w-56 h-56 md:w-64 md:h-64 rounded-full overflow-hidden border border-slate-200 dark:border-slate-800 bg-white/45 dark:bg-slate-900/40 relative shadow-inner group-hover:border-teal-500/50 transition-all duration-300">
              <img 
                src={Profile} 
                className="w-full h-full object-cover scale-100 group-hover:scale-105 transition-transform duration-500" 
                alt="Amiola Demilade" 
              />  
            </div>
          </div>
        </motion.div>

      </div>
      
      <WhatsAppLink />
    </div>
  );
}

export default HomeComponent;
